import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  leftIcon?: React.ReactNode;
  leftIconClassName?: string;
  rightElement?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, leftIcon, leftIconClassName, rightElement, id, ...props }, ref) => {
    return (
      <div className="relative group">
        {leftIcon && (
          <span
            className={cn(
              'material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-focus-within:text-primary transition-colors',
              leftIconClassName,
            )}
          >
            {leftIcon}
          </span>
        )}
        <input
          type={type}
          className={cn(
            'form-input block w-full py-2.5 rounded-lg border text-sm transition-all',
            'placeholder-gray-400',
            'bg-white dark:bg-surface-dark-elevated',
            'border-gray-200 dark:border-gray-700',
            'text-text-primary dark:text-white',
            'px-3',
            leftIcon && 'pl-10',
            rightElement && 'pr-10',
            className,
            // Error styles come last to ensure they override any conflicting styles from className
            error && 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 dark:border-red-500',
            !error && 'focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none',
          )}
          ref={ref}
          id={id}
          {...props}
        />
        {rightElement && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightElement}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
